import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ROUTES } from "@/app/routes";
import { applyNewSession } from "@/features/identity/apply-new-session";
import { AccountPicker } from "@/features/identity/components/account-picker";
import { type AccountCandidate, googlePickAccount } from "@/features/identity/identity.api";
import { isSafeOAuthReturnTo } from "@/shared/auth/oauth-return-to";
import { LogoLockup } from "@/shared/components/brand/logo-mark";
import { Button } from "@/shared/components/ui/button";

const route = getRouteApi(ROUTES.auth.googlePickAccount.id);

/**
 * Google sign-in resolved multiple Users for the same email across Accounts.
 * The shore redirected here with `?handoff=<signed payload>` whose JSON
 * payload includes the candidate list. We decode the candidates client-side
 * (the payload is HMAC-signed; user can read but not modify).
 */
export function GoogleSignInPickAccountRoute() {
  const { handoff } = route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [candidates, setCandidates] = useState<AccountCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = useMutation({
    mutationFn: googlePickAccount,
    onSuccess: async ({ returnTo }) => {
      if (isSafeOAuthReturnTo(returnTo)) {
        // reason: full reload into the consent page so it boots with the new
        // session, the same reason the login form reloads after sign-in.
        window.location.assign(returnTo);
        return;
      }
      await applyNewSession(qc);
      await navigate({ to: "/", search: {} });
    },
  });

  useEffect(() => {
    if (!handoff) {
      void navigate({ to: "/auth/error", search: { reason: "oauth-failed" } });
      return;
    }
    try {
      const dot = handoff.indexOf(".");
      const bodyB64 = handoff.slice(0, dot);
      const json = JSON.parse(
        new TextDecoder().decode(
          Uint8Array.from(atob(bodyB64.replace(/-/g, "+").replace(/_/g, "/")), (c) =>
            c.charCodeAt(0),
          ),
        ),
      ) as { candidates?: AccountCandidate[] };
      setCandidates(json.candidates ?? []);
    } catch {
      setError("Could not parse sign-in data. Try again.");
    }
  }, [handoff, navigate]);

  if (!handoff) return null;
  if (error) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 px-6 py-12 text-center">
        <p className="text-sm text-destructive-text">{error}</p>
        <Button onClick={() => navigate({ to: "/login", search: {} })}>Back to sign in</Button>
      </div>
    );
  }
  if (!candidates) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md items-center justify-center px-6 py-12">
        <p className="text-sm text-text-secondary">Loading accounts…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-6 py-12">
      <LogoLockup className="w-fit" />
      <AccountPicker
        reason="Your Google email is on more than one Pulsewise account."
        candidates={candidates}
        pending={pick.isPending}
        error={pick.isError ? "Could not sign you in to that account. Try again." : null}
        onPick={(userId) => pick.mutate({ handoff, userId })}
      />
    </div>
  );
}
